// The main Reports menu (Order Timeline, Supplier Performance, Activity Log) is hidden for every role and organisation.
// Nothing is deleted: the screens, their saved settings and data stay, and the Activity Log keeps recording every change.
// One switch (Falcon Admin Console → Falcon Settings → Main Reports menu) turns the whole menu back on, or one report.

export const MAIN_REPORTS = [
  { id: "timeline", label: "Order Timeline", href: "/app/reports/timeline", help: "order-timeline" },
  { id: "supplierperformance", label: "Supplier Performance", href: "/app/performance", help: "supplier-performance" },
  { id: "activity", label: "Activity Log", href: "/app/activity", help: "activity-log" },
] as const;
export type MainReportId = (typeof MAIN_REPORTS)[number]["id"];
export interface ReportsMenu { menu: boolean; reports: Record<MainReportId, boolean> }

/** Hidden unless switched on. */
export const REPORTS_MENU_DEFAULT: ReportsMenu = { menu: false, reports: { timeline: false, supplierperformance: false, activity: false } };

export function normalizeReportsMenu(v: unknown): ReportsMenu {
  const x = (v && typeof v === "object" ? v : {}) as any;
  const r = x.reports && typeof x.reports === "object" ? x.reports : {};
  return { menu: x.menu === true, reports: Object.fromEntries(MAIN_REPORTS.map((m) => [m.id, r[m.id] === true])) as Record<MainReportId, boolean> };
}
/** Is this report shown? The whole menu switched on shows them all. */
export function reportShown(cfg: ReportsMenu | null | undefined, id: MainReportId): boolean {
  const c = cfg || REPORTS_MENU_DEFAULT;
  return c.menu || c.reports[id] === true;
}
/** The report an address opens (null when it is not one of them). */
export function reportOfPath(path: string): MainReportId | null {
  const p = path.split("?")[0];
  if (p === "/app/reports/timeline" || p.startsWith("/app/reports/timeline/")) return "timeline";
  if (p === "/app/performance" || p.startsWith("/app/performance/")) return "supplierperformance";
  if (p === "/app/activity" || p.startsWith("/app/activity/")) return "activity";
  return null;
}
/** The help pages that describe a hidden report (kept, not shown). */
export function hiddenHelpPages(cfg: ReportsMenu | null | undefined): string[] {
  return MAIN_REPORTS.filter((m) => !reportShown(cfg, m.id)).map((m) => m.help);
}
export const NOT_AVAILABLE = "This report is not available at the moment.";
const hiddenLabels = (cfg: ReportsMenu | null | undefined) => MAIN_REPORTS.filter((m) => !reportShown(cfg, m.id)).map((m) => m.label);
/** Does this help text send people to a hidden report? (The quick-start guides leave such a line out.) */
export function mentionsHiddenReport(text: string, cfg: ReportsMenu | null | undefined): boolean {
  return hiddenLabels(cfg).some((l) => text.includes(l));
}
/** Help text with a passing mention of a hidden report left out ("…, agents and Supplier Performance." → "…, agents."). */
export function withoutHiddenReports(text: string, cfg: ReportsMenu | null | undefined): string {
  return hiddenLabels(cfg).reduce((t, l) => t.replace(new RegExp(`(,| and)\\s+${l}`, "g"), ""), text);
}
